import React, { createContext, Component } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import humps from 'humps';
import ErrorTracker from 'error_tracker';
import ApiRequest from 'core/api/request';
import { promiser, trackFbEvent, trackEvent, getUser, parseCouponError, JsonStorage } from 'core/utils';
import { default as BrazeUtil, getBrazeCartProps } from 'core/utils/Braze';
import { getCartCount, filterEmptyCarts, checkIfProductInstanceIsInCart } from 'cart/utils';
import transformSubscriptionTerms from 'product/components/TermsPicker/transformSubscriptionTerms';
import PersonalizeUtil from 'personalize_util';

// Max number of carts to fetch
const MAX_CARTS = 20;

export const productStatus = {
	ALL_ECOM: "ALL_ECOM",
	SOME_ECOM: "SOME_ECOM",
	ALL_SUBS: "ALL_SUBS",
};

const cartProviderDefaultProps = {
	allowMulticart: false, // `allow_multicart=t` in URL to enable beta
	testModeOnly: false, // `test_mode=t` in URL to checkout with only test_mode CJP carts
	checkout: false, // When this and `multicart` True, only stage Cratejoy Payments carts
	multicart: false, // when more than one cart *can be* staged for cart (via base route /cart/ or /checkout/cart/)
	activeCartId: null, // the one user is interacting with (on cart page)
	activeCartInstanceVariantMap: null, // map of product_instance_id to comma-separated list of variant values
	activeInstanceTermsMap: null,
	carts: [],
	cartCount: 0,
	loading: true,
	// Are we currently calculating tax/ship price?
	loadingTaxShip: false,
};

const CartContext = createContext({ ...cartProviderDefaultProps });
CartContext.displayName = 'CartContext';

/**
 * This context holds pertinent info to hydrate the ShopBag, Cart & Checkout
 * It fetches & interacts primarily with the Carts API
 * And also fetches product instances info for variants & term selection
 * This are listeners to communicate with ShopBag (since it's outside this context)
 * There are functions that: add/remove products or coupons to cart, recalculate taxes/fees, etc
 * NOTE: "staged" carts refers the active cart(s) the user is viewing on cart/checkout page for purchase
 */
export class CartProvider extends Component {
	state = {
		...cartProviderDefaultProps,
		...this.props.value,
	}

	componentDidMount() {
		this.setStateFromQueryParams();
		this.fetchCarts();

		// Listen for updates
		window.addEventListener('ShopBag.removeItem', this.handleShopBagRemoveItem);
	}

	componentDidUpdate() {
		// Quick deep clone of carts:
		const carts = JSON.parse(JSON.stringify(this.state.carts));
		// Dispatch updates to ShopBag
		const evt = new CustomEvent('ShopBag.setCarts', {
			detail: { carts }
		});
		window.dispatchEvent(evt);
	}

	static isAllowMulticart() {
		if (typeof window !== 'undefined') {
			// Window isn't defined during SSR
			return window?.location?.search?.includes('allow_multicart=t');
		}
		return false;
	}

	static isTestMode() {
		if (typeof window !== 'undefined') {
			// Window isn't defined during SSR
			return window?.location?.search?.includes('test_mode=t');
		}
		return false;
	}

	setStateFromQueryParams = () => {
		let nextState = {};
		if (CartProvider.isAllowMulticart()) {
			nextState.allowMulticart = true;
		}

		if (CartProvider.isTestMode()) {
			nextState.testModeOnly = true;
		}

		if (nextState?.allowMulticart || nextState?.testModeOnly) {
			this.setState(nextState);
		}
	}

	handleShopBagRemoveItem = (evt) => {
		const { detail: cartProductId } = evt;
		this.removeFromCart(cartProductId);
	}

	/**
	 * Fetches all carts regardless of cartId. (to fully populate the ShopBag on `/cart` page)
	 * Will also fetch by cartId(s) when missing from list.
	 * Will fetch variant info for active or multi-carts (for cart page)
	 * Sets `carts` in state
	 *
	 * @param {Bool} cartsOnly - True, when we *only* want to fetch carts (and skip variant fetch)
	 * @return {Promise}
	 */
	fetchCarts = async (cartsOnly=false) => {
		const [err, response] = await promiser(
			ApiRequest.get('/carts/', {
				limit: MAX_CARTS,
				page: 1,
				live_only: true,
				skip_empty: true,
			})
		);

		if (err) {
			console.log(err);
			ErrorTracker.captureException(err);
			return;
		}

		const results = response?.results || [];

		// Find (or fetch) the "active cart" and place atop list
		const activeCartId = this.state?.activeCartId;
		const cartIds = results?.map(c => c.id) || [];
		ErrorTracker.addBreadcrumb({
			category: 'cart',
			message: 'CartContext: fetchCarts [activeCartId, ...results.cartIds]',
			data: {
				carts: [activeCartId, ...cartIds]
			},
		});

		// Move "active cart" to top of list
		let activeCart;
		if (activeCartId) {
			let cartIx = results.findIndex(c => c.id === activeCartId);
			if (cartIx >= 0) {
				// remove from the list of carts (to place atop)
				let [foundCart] = results.splice(cartIx, 1);
				activeCart = foundCart;
			} else {
				// fetch one cart in case it was not returned by limited query above
				const cart = await this.fetchCartById(activeCartId);
				if (cart) {
					activeCart = cart;
				}
			}
			// place active cart atop list
			if (activeCart) {
				results.unshift(activeCart);
			}
		}

		// `skip_empty` should make sure there are cart_products with quantity>0
		// `filterEmptyCarts` is a safeguard
		const carts = filterEmptyCarts(results).slice(0, MAX_CARTS);
		const count = getCartCount(carts);

		// Set cookie for ShopBag BagCounter
		Cookies.set('cartCount', count || '', { expires: 90 });

		if (!cartsOnly) {
			// fetch product instances (variant info) for staged carts
			const { allowMulticart, multicart, checkout, testModeOnly } = this.state;
			const inCartOrCheckout = activeCart?.id || multicart;
			if (inCartOrCheckout) {
				let stagedCarts = activeCart ? [activeCart] : (checkout ? CartProvider.findCJPCarts(carts, testModeOnly) : carts);
				if (!(multicart && allowMulticart) && stagedCarts?.length > 1) {
					stagedCarts = stagedCarts.slice(0, 1);
				}
				if (stagedCarts?.length > 0) {
					this.fetchProductInstances(stagedCarts);
				}
			}
		}

		this.setState({
			carts: [...carts],
			loading: false,
		});
	}

	fetchCartById = async (cartId) => {
		const [err, cart] = await promiser(ApiRequest.get(`/carts/${cartId}/`));
		if (err) {
			console.log(err);
			ErrorTracker.captureException(err);
			return;
		}
		return cart;
	}

	/**
	 * Calculate tax/shipping/fees
	 *
	 * @param {Object} [keyword] params
	 * 		@param {Array} cartIds - array of cart ids to recalc
	 * 		@param {Array} addressId - customer address id to run against
	 * 		@param {Object} address - for guest { country, state, street, zipCode }
	 * @return {Promise} Resolves with <Array>
	 */
	calculateTaxShip = async ({ cartIds, addressId, address = {} }) => {
		if (!(cartIds?.length > 0)) return;
		const data = {};

		if (addressId) {
			data.addressId = addressId;
			Cookies.set("xoLastUsedAddress", addressId);
		} else {
			const hasRequiredFields = this.checkRequiredAddressFields(address);
			if (!hasRequiredFields) {
				return;
			}
			data.street = address.street;
			data.state = address.state;
			data.zipCode = address.zipCode;
			data.country = address.country;
		}

		this.setState({ loadingTaxShip: true });

		// Iterate over carts & wait for all responses
		const requests = cartIds.map((cartId) => promiser(
			ApiRequest.put(`/carts/${cartId}/`, data)
		));
		const results = await Promise.all(requests);

		let { carts } = this.state;
		let nextState = { loadingTaxShip: false };
		results.forEach(([err, cart]) => {
			if (err) {
				console.log(err);
				ErrorTracker.captureException(err);
				return; // continue
			}

			const cartIndex = carts.findIndex(c => c.id === cart.id);
			if (cartIndex >= 0) {
				// Update cart in state
				carts[cartIndex] = cart;
				nextState.carts = carts;
			}
		});

		this.setState(nextState, () => {
			// Broadcast event when tax/ship price has updated
			const evt = new CustomEvent('TaxShipUpdated', {
				detail: { cartIds }
			});
			window.dispatchEvent(evt);
		});
		return carts;
	}

	checkRequiredAddressFields = (address = {}) => {
		const { zipCode, street, state, country } = address;
		const required = [zipCode, street, state, country];
		const hasRequiredFields = required.filter(el => Boolean(el)).length === required.length;
		return hasRequiredFields;
	}

	/**
	 * Fetch one cart by store id
	 * In some situations it's possible `cart` is not in `CartContext`
	 * but does exist (due to limit on API calls)
	 *
	 * @param {Number} storeId - fetch cart for this store
	 * @return {Object} cart
	 */
	fetchCartByStore = async (storeId) => {
		// fetch one cart in case it was not returned by limited query above
		const [e, resp] = await promiser(ApiRequest.get(`/carts/`, {
			store_id: storeId,
			page: 1,
			limit: 1,
		}));

		if (e || !resp?.results?.length) {
			return;
		}

		// Add to state...
		let cart = resp.results[0];
		let { carts } = this.state;
		const cartIndex = carts.findIndex(c => c.id === cart.id);

		if (cartIndex >= 0) {
			// remove, to be replaced atop list
			carts.splice(cartIndex, 1);
		}

		// Add atop list
		carts.unshift(cart);

		const cartCount = getCartCount(carts);

		Cookies.set('cartCount', cartCount || '', { expires: 90 });
		this.setState({ carts, cartCount });
		return cart;
	}

	/**
	 * Fetch product instances for the staged carts.
	 * Sets variant & terms info in state as:
	 * activeCartInstanceVariantMap & activeInstanceTermsMap
	 */
	fetchProductInstances = async (carts=[]) => {
		const numProducts = carts.reduce((total, cart) => (
			total + (cart?.products?.length || 0)
		), 0);
		if (!(numProducts > 0)) return;

		const instanceIds = carts.reduce((ids, cart) => {
			let piIds = cart.products.map(cp => cp.productInstanceId);
			return ids.concat(piIds);
		}, []);
		const limit = Math.max(instanceIds.length, 10);
		const [err, response] = await promiser(
			ApiRequest.get(`/product_instances/`, {
				id__in: instanceIds.join(","),
				with_variants: true,
				page: 1,
				per_page: limit,
				limit: limit,
			})
		);

		if (err) {
			console.error(err);
			return;
		}

		const piTermsMap = {};
		const piVariantMap = {};
		response.results.forEach((pi) => {
			if (pi?.variants?.length > 0) {
				piVariantMap[pi.id] = pi.variants.map(([name, val]) => val).join(", ");
			}
			if (pi?.termPrices?.length > 0) {
				piTermsMap[pi.id] = transformSubscriptionTerms({
					billing: pi.product?.billing || {},
					termPrices: pi.termPrices || [],
				})[0];
			}
		});

		this.setState({
			activeCartInstanceVariantMap: Object.keys(piVariantMap).length > 0 ? piVariantMap : null,
			activeInstanceTermsMap: Object.keys(piTermsMap).length > 0 ? piTermsMap : null,
		});
	}

	/**
	 * Return the active cart(s) defined by `activeCartId`
	 * When no `activeCartId` is set, fallback to first cart
	 * Will return all (multicart-enabled) with `allow_multicart=t` param
	 *
	 * @return {Array} activeCarts - List of <Cart> objects
	 */
	getActiveCarts = () => {
		const { allowMulticart, activeCartId, carts, testModeOnly } = this.state;
		if (carts.length === 0) return [];
		if (!activeCartId) {
			const cjpCarts = CartProvider.findCJPCarts(carts, testModeOnly);
			if (cjpCarts?.length > 0 && allowMulticart) {
				return cjpCarts;
			}
			return [carts[0]];
		}
		const activeCart = carts.find(c => c.id === activeCartId);
		return activeCart ? [activeCart] : [];
	}

	static findCJPCarts = (carts=[], testMode=false) => carts.filter(c => c.store.cratejoyPaymentsConfigured === true && c.store.testMode === testMode);

	/**
	 * Split carts into two lists based on CJP configuration
	 *
	 * @return {Array} [cratejoyPaymentsCarts, otherCarts, allCarts]
	 */
	getCartsByCratejoyPayments = () => {
		const { allowMulticart, carts } = this.state;
		if (carts.length === 0) return [];
		if (!allowMulticart) {
			// Will return 1 cart (active or first)
			const activeCarts = this.getActiveCarts();
			return [null, null, activeCarts, activeCarts];
		}
		const cjpCarts = CartProvider.findCJPCarts(carts);
		const cjpCartsTestMode = CartProvider.findCJPCarts(carts, true);
		const otherCarts = CartProvider.findNonCJPCarts(carts);
		return [cjpCarts, cjpCartsTestMode, otherCarts, carts];
	}

	static findNonCJPCarts = (carts=[]) => carts.filter(c => !c.store.cratejoyPaymentsConfigured);

	addProductToCart = async ({ productInstanceId, subscriptionTypeTermId, quantity, listing, clear = false }) => {
		quantity = quantity || 1;

		// Actually add product to cart via API:
		const [err, cart] = await promiser(
			ApiRequest.post('/cart_products/', {
				productInstanceId,
				subscriptionTypeTermId,
				quantity,
				allowAddCartProduct: true,
			})
		);

		if (err) {
			// TODO: When product already in cart, use PUT to modify
			// NOTE: If we want to support +1 to quantity here...
			// Otherwise, just open ShopBag bc it's already there??
			console.warn(err);
			throw err;
		}

		if (clear) {
			const otherCartProducts = cart.products.filter((cp) => {
				if (cp.productInstanceId !== productInstanceId) {
					// different PI = different product
					return true;
				}

				if (subscriptionTypeTermId) {
					// same pi, different sub product = different product
					return (cp?.subscriptionTypeTermId !== subscriptionTypeTermId);
				}

				// same pi = same product
				return false;
			});
			const results = await Promise.all(
				otherCartProducts.map((cp) => promiser(
					this.removeFromCart(cp.id)
				))
			);
			trackEvent("Clear Then AddToCart", { fullstory: [] });
		}

		// Add to state...
		let { carts } = this.state;
		const cartIndex = carts.findIndex(c => c.id === cart.id);

		if (cartIndex >= 0) {
			carts[cartIndex] = cart;
		} else {
			carts.unshift(cart);
		}

		const cartCount = getCartCount(carts);

		this.setState({ carts, cartCount });
		Cookies.set('cartCount', cartCount || '', { expires: 90 });

		this.firePixels({ listing, productInstanceId, cart, subscriptionTypeTermId });

		return cart;
	}

	/**
	 * Fire all "Add to Cart" events, third-party, social & ad conversion, etc
	 * @param {Object} data
	 */
	firePixels = async ({ listing, productInstanceId, cart, subscriptionTypeTermId }) => {
		// Conversions (Ads)
		trackEvent("conversion", {
			gtag: [{ send_to: "AW-957505549/k4EPCNOrg8kBEI3AycgD" }]
		});
		trackEvent("add-to-cart", {
			gtag: [{
				'event_category': 'optimize',
				'event_label': 'add-to-cart',
				'value': 'add-to-cart',
			}]
		});
		if (listing?.id) {
			trackFbEvent('AddToCart', {
				external_id: window.cj?.user?.id,
				content_type: 'product',
				content_ids: [listing.id],
			});
		}

		// Stats
		trackEvent('PplAddToCart', {
			heap: [{ listingId: listing?.id, productInstanceId: productInstanceId, subscriptionTypeTermId: subscriptionTypeTermId }],
			statjoy: [{ cus1: listing?.id, cus3: productInstanceId, cus4: subscriptionTypeTermId }],
			fullstory: [],
		});

		// Braze
		const product = checkIfProductInstanceIsInCart({ productInstanceId, carts: [cart], subscriptionTypeTermId });
		if (listing && getUser()) {
			const brazeListingProps = humps.camelizeKeys(listing);
			const brazeProps = getBrazeCartProps(brazeListingProps, product);
			BrazeUtil.addToCartPPL(brazeProps);
		}

		// JustUno
		const justUnoData = {
			productid: product.id,
			name: product.productInstance.product.name,
			quantity: product.quantity,
			price: parseInt(product.price, 10) / 100,
		};

		trackEvent("cartItemAdd", {
			justuno: [justUnoData]
		});

		// Amazon Personalize (Recs)
		if(listing) {
			PersonalizeUtil.onAddToCart(listing.id);
		}
	}

	/**
	 * Adds a coupon via couponCode to the the given cartId.
	 * If multicartAdd set `true`, will attempt to add couponCode to all CJP carts for the requesting customer via API.
	 * If multicartAdd set `false`, will only attempt given cartId and will remove all other coupons from cart.
	 * @param {Number} cartId
	 * @param {String} couponCode
	 * @param {Bool} multicartAdd
	 * @returns
	 */
	addCouponToCart = async (cartId, couponCode, multicartAdd=false) => {
		if (!cartId || !couponCode) return;

		let { carts } = this.state;
		const cartIndex = carts.findIndex(c => c.id === cartId);

		if (cartIndex < 0) return;

		const existingCartCouponIds = carts[cartIndex]?.coupons?.map(c => c.id);
		if(carts[cartIndex]?.coupons?.map(c => c.codeUsed)?.includes(couponCode)) {
			const e = new Error(`${couponCode} already applied to cart`);
			e.errors = [`${couponCode} already applied to cart`];
			throw e;
		}

		const listingVersionId = carts[cartIndex]?.listing?.id;
		// Manually create ApiRequest instance to config POST req query params
		const request = ApiRequest.create('post', `/carts/${cartId}/coupons/`, {
			couponCode,
			listingVersionId,
		});
		if (multicartAdd) {
			request.query({
				"allow_multicart": true,
			});
		} else {
			request.query({
				"force_exclusive": true,
			});
		}
		// Add coupon to cart via API:
		const [err, cart] = await promiser(
			request.send()
		);

		// Handle any potential 500s here
		const { fetchResponse } = err || {};
		const { status } = fetchResponse || {};
		if (status >= 500 && status <= 599) {
			trackEvent("add_coupon_to_cart_error", {
				fullstory: [{ status_str: `${status}` }],
				statjoy: [{ cus4: status, cus5: JSON.stringify(err) }],
			});
			throw { errors: ['Server error'] };
		}

		if (!multicartAdd ) {
			if (err) {
				// If there was an error from earlier, throw it to handle in UI
				console.warn(err);
				trackEvent("add_coupon_to_cart_error", {
					fullstory: [{
						"multicartAdd_bool": multicartAdd,
						"couponCode_str": couponCode,
						"message_str": parseCouponError(err) || "",
					}],
					heap: [{ cartId: cartId, couponCode: couponCode, multicart: false }],
					statjoy: [{ cus1: cartId, cus2: couponCode, cus3: "singlecart" }],
				});
				throw err;
			} else {
				trackEvent("add_coupon_to_cart", {
					fullstory: [],
					heap: [{ cartId: cartId, couponCode: couponCode, multicart: false }],
					statjoy: [{ cus1: cartId, cus2: couponCode, cus3: "singlecart" }],
				});
				// If we have cart coupon, then delete those first
				// Checkout currently only supports one coupon per order, so we remove the existing ones:
				if(existingCartCouponIds) {
					const allResp = await Promise.allSettled(
						existingCartCouponIds.map(cartCouponId => ApiRequest.delete(`/carts/${cartId}/coupons/${cartCouponId}/`))
					);
					// For not multicart, update this cart state
					const updatedCart = await this.fetchCartById(cartId);
					// Update cart in state
					carts[cartIndex] = updatedCart ? updatedCart : cart;
					this.setState({ carts });
				}
			}

			return cart;
		} else {
			// For multicart update all carts (in case multiple cart state updates)
			await this.fetchCarts(true);
			// If there was an error from earlier, throw so we
			// can handle it in the UI
			if (err) {
				trackEvent("add_coupon_to_cart_error", {
					fullstory: [{
						"multicartAdd_bool": multicartAdd,
						"couponCode_str": couponCode,
						"message_str": parseCouponError(err) || "",
					}],
					heap: [{ cartId: cartId, couponCode: couponCode, multicart: true }],
				});
				throw err;
			} else {
				trackEvent("add_coupon_to_cart", {
					fullstory: [],
					heap: [{ cartId: cartId, couponCode: couponCode, multicart: true }],
					statjoy: [{ cus1: cartId, cus2: couponCode, cus3: "multicart" }],
				});
			}
			return;
		}
	}

	removeFromCart = async (cartProductId, options={}) => {
		const {
			refetchCart=false,
			silent=false, // When true, no state updates
		} = options;

		// Actually delete
		await ApiRequest.delete(`/cart_products/${cartProductId}/`);

		if (silent) {
			// Skip the state updates & pixel triggers
			return;
		}

		// And remove from state...
		let removedCartProduct, lastRemovedInfo = {};
		let { carts } = this.state;
		for (let i = 0; i < carts.length; i++) {
			let cpIndex = carts[i].products.findIndex(cp => cp.id === cartProductId);
			if (cpIndex >= 0) {
				removedCartProduct = carts[i].products[cpIndex];
				lastRemovedInfo = {
					slug: removedCartProduct?.listing?.slug,
					store: carts[i]?.store?.name,
				};
				// remove product from its cart
				carts[i].products = carts[i].products.filter(cp => cp.id !== cartProductId);
				// When this cart has remaining products...
				// Fetch the cart so we have the correct prices, discounts, etc
				// NOTE: activeCartId is cartId being staged on cart page (remove from shopbag while on cart page)
				let refresh = options?.refetchCart || (this.state.activeCartId === carts[i].id);
				if (refresh && carts[i].products.length > 0) {
					let refetchCartId = carts[i].id;
					const updatedCart = await this.fetchCartById(refetchCartId);
					if (updatedCart) {
						// Update the cart in state
						carts[i] = updatedCart;
					}
				}
				break;
			}
		}

		carts = filterEmptyCarts(carts);

		const cartCount = getCartCount(carts);

		if (lastRemovedInfo?.slug) {
			// Used by EmptyCart
			const storage = new JsonStorage('lastRemovedFromCart', true);
			storage.save(lastRemovedInfo);
		}

		this.setState({ carts, cartCount });
		Cookies.set('cartCount', cartCount || '', { expires: 90 });

		// Pixels etc
		const variantString = removedCartProduct?.subscriptionTypeTerm?.id ? removedCartProduct.productInstance.id + '_' + removedCartProduct.subscriptionTypeTerm.id : removedCartProduct.productInstance.id;
		trackEvent("remove_from_cart", {
			braze: [{
				listingId: removedCartProduct.productInstance.product.id,
				variant: variantString,
				productInstanceId: removedCartProduct.productInstance.id,
				subscriptionTypeTermId: removedCartProduct?.subscriptionTypeTerm?.id,
				quantity: 1,
			}],
			fullstory: [],
			heap: [{
				productId: removedCartProduct.productInstance.product.id,
				variant: variantString,
				productInstanceId: removedCartProduct.productInstance.id,
				subscriptionTypeTermId: removedCartProduct?.subscriptionTypeTerm?.id,
				quantity: 1,
			}],
			statjoy: [{ cus1: removedCartProduct.productInstance.product.id, cus2: variantString, cus3: removedCartProduct.productInstance.id, cus4: removedCartProduct?.subscriptionTypeTerm?.id }],
		});
	}

	/**
	 * Removes an existing CartCoupon from a given Cart by ID
	 * @param {Number} cartId
	 * @param {Number} cartCouponId
	 */
	removeCartCouponFromCart = async (cartId, cartCouponId) => {
		// Remove the coupon from the cart
		await ApiRequest.delete(`/carts/${cartId}/coupons/${cartCouponId}/`);
		trackEvent("remove_coupon_from_cart", {
			fullstory: [],
			heap: [{ cartId: cartId }],
			statjoy: [{ cus1: cartId }],
		});

		// Go get the cart so that we have the latest and correct prices
		const updatedCart = await this.fetchCartById(cartId);

		// Update the cart in state
		const { carts } = this.state;
		const cartIndex = carts.findIndex(c => c.id === cartId);
		const newCartsState = [...carts];
		newCartsState[cartIndex] = updatedCart;
		this.setState({ carts: newCartsState });
	}

	getEcomStatus = () => {
		const carts = this.getActiveCarts();
		const hasAnyEcom = carts.some(cart => cart.products.some(cp => !cp.subscriptionTypeTermId));
		const hasSubs = carts.some(cart => cart.products.some(cp => cp.subscriptionTypeTermId));
		const hasAllEcom = !hasSubs;
		if (hasAllEcom) return productStatus.ALL_ECOM;
		if (hasAnyEcom) return productStatus.SOME_ECOM;
		return productStatus.ALL_SUBS;
	}

	getCartEcomStatus = (cartId) => {
		const { carts } = this.state;
		const cart = carts.find(c => c.id === cartId);
		if (! cart) return;

		const hasAnyEcom = cart.products.some(cp => !cp.subscriptionTypeTermId);
		const hasSubs = cart.products.some(cp => cp.subscriptionTypeTermId);
		const hasAllEcom = !hasSubs;

		if (hasAllEcom) return productStatus.ALL_ECOM;
		if (hasAnyEcom) return productStatus.SOME_ECOM;
		return productStatus.ALL_SUBS;
	}

	render() {
		const {
			state,
			addProductToCart,
			addCouponToCart,
			fetchCartByStore,
			removeFromCart,
			removeCartCouponFromCart,
			getActiveCarts,
			getCartsByCratejoyPayments,
			calculateTaxShip,
			checkRequiredAddressFields,
			getEcomStatus,
			getCartEcomStatus,
		} = this;
		const { children } = this.props;
		const value = {
			...state,
			addProductToCart,
			addCouponToCart,
			fetchCartByStore,
			removeFromCart,
			removeCartCouponFromCart,
			getActiveCarts,
			getCartsByCratejoyPayments,
			calculateTaxShip,
			checkRequiredAddressFields,
			getEcomStatus,
			getCartEcomStatus,
		};

		return (
			<CartContext.Provider value={value}>
				{children}
			</CartContext.Provider>
		);
	}
}

CartProvider.sharedPropTypes = {
	activeCartId: PropTypes.number,
	activeCartInstanceVariantMap: PropTypes.object,
	activeInstanceTermsMap: PropTypes.object,
	carts: PropTypes.arrayOf(PropTypes.object),
	cartCount: PropTypes.number,
	loading: PropTypes.bool,
	loadingTaxShip: PropTypes.bool,
};

CartProvider.propTypes = {
	children: PropTypes.node.isRequired,
	value: PropTypes.shape({
		...CartProvider.sharedPropTypes,
	}),
};

CartProvider.defaultProps = {
	value: {
		...cartProviderDefaultProps,
	},
};

/*
* Provide children down the tree with access to this data
*/
export const CartConsumer = CartContext.Consumer;

export default CartContext;
